import React, { useMemo } from 'react';
import _ from 'lodash';
import {
  Text,
  View,
  Page,
  Document,
  StyleSheet,
  Svg,
  Path,
  Font,
  Image
} from '@react-pdf/renderer';

import InvoiceItemsTable from '../invoice-table';

import { BookingLine, TransactionData, TransactionPayment } from 'services/api/type/booking.type';
import { MyInfoType } from 'services/api/type/user.type';
import { toLocalTime } from 'utils';
import { currencyFormatter } from 'utils/currency';
import { CHARGE_TYPE } from 'components/modal/bill-pdf-modal';
import { BranchesType } from 'services/api/type/branch.type';
import {
  PAYMENT_AND_REFUND_TRANSACTION_CODE,
  ROOM_CHARGE_TRANSACTION_CODE,
  SERVICE_TRANSACTION_CODE
} from 'components/common/constant';
import { ResponseGroupBookingDetail } from 'services/api/type/group.type';
import { useTranslation } from 'react-i18next';

// Register font
Font.register({
  family: 'Inter',
  fonts: [
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyeMZhrib2Bg-4.ttf',
      fontWeight: 100
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuDyfMZhrib2Bg-4.ttf',
      fontWeight: 200
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuOKfMZhrib2Bg-4.ttf',
      fontWeight: 300
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuLyfMZhrib2Bg-4.ttf',
      fontWeight: 400
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuI6fMZhrib2Bg-4.ttf',
      fontWeight: 500
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuGKYMZhrib2Bg-4.ttf',
      fontWeight: 600
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuFuYMZhrib2Bg-4.ttf',
      fontWeight: 700
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuDyYMZhrib2Bg-4.ttf',
      fontWeight: 800
    },
    {
      src: 'https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuBWYMZhrib2Bg-4.ttf',
      fontWeight: 900
    }
  ]
});

const styles = StyleSheet.create({
  page: {
    fontSize: 12,
    paddingTop: 20,
    paddingLeft: 20,
    paddingRight: 20,
    lineHeight: 1.5,
    flexDirection: 'column'
  },

  spaceBetween: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: '#000'
  },
  flex: {
    display: 'flex',
    flexDirection: 'row',
    gap: '8px'
  },

  image: {
    width: 60,
    height: 50,
    marginTop: 10,
    marginLeft: 40
  },

  titleContainer: { flexDirection: 'row', marginTop: 8, marginBottom: 16 },
  billContainer: { flexDirection: 'column', paddingBottom: '12px' },
  billRow: {
    flexDirection: 'row',
    columnGap: '108px'
  },
  billCol: {
    flexDirection: 'column',
    gap: '8px',
    justifyContent: 'flex-start'
  },
  billColItem: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    columnGap: '12px'
  },
  billRowItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    columnGap: '8px',
    marginTop: '8px'
  },

  totalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: '#000'
  },

  signatureContainer: {
    flexDirection: 'row',
    marginTop: 28,
    justifyContent: 'space-evenly'
  },
  signatureItem: {
    flexDirection: 'column',
    rowGap: '8px'
  },

  logo: { width: 90 },
  text: { fontSize: 11, fontFamily: 'Inter' },
  textExSmallBold: { fontSize: 11, fontWeight: 600, fontFamily: 'Inter' },
  textBold: { fontSize: 14, fontWeight: 600, fontFamily: 'Inter' },
  textSmallBold: { fontSize: 12, fontWeight: 600, fontFamily: 'Inter' }
});

interface Props {
  bookingLineDetail?: BookingLine | undefined;
  orderDetail?: TransactionData | undefined;
  me: MyInfoType | undefined;
  dataChange: any;
  signatureStaff: string;
  signatureGuest: string;
  currentBranch: BranchesType | undefined;
  dataGroup?: ResponseGroupBookingDetail | undefined;
  transactionPayment: TransactionPayment[] | undefined;
  invoiceError?: string;
  showInvoiceError?: boolean;
}

export default function BillPDFv2({
  bookingLineDetail,
  dataChange,
  signatureStaff,
  signatureGuest,
  currentBranch,
  orderDetail,
  dataGroup,
  transactionPayment,
  invoiceError,
  showInvoiceError
}: Props) {
  const { t } = useTranslation();
  const Logo = () => (
    <Svg width="71" height="57" viewBox="0 0 71 57" fill="none">
      <Path
        d="M27.4297 10.9824C29.3562 10.9824 30.918 9.39243 30.918 7.43109C30.918 5.46974 29.3562 3.87976 27.4297 3.87976C25.5032 3.87976 23.9414 5.46974 23.9414 7.43109C23.9414 9.39243 25.5032 10.9824 27.4297 10.9824Z"
        fill="#2E3E54"
      />
      <Path
        d="M32.0671 41.665V19.8676C32.0671 14.4051 29.1785 13.5126 29.1785 13.5126H22.3688C22.3688 13.5126 25.2574 14.3525 25.2574 19.8676V41.665C25.2574 47.1275 28.146 48.02 28.146 48.02H34.9557C34.9557 48.02 32.0671 47.1801 32.0671 41.665Z"
        fill="#2E3E54"
      />
      <Path
        d="M56.7265 13.5125H51.6014V34.97C48.406 24.9959 43.1071 22.4329 43.1071 16.8734C43.1071 14.2473 44.3449 13.5114 44.3449 13.5114H36.2974V48.0211H37.226V19.9201C40.3211 30.3194 49.9168 36.8859 49.9168 44.7116C49.9168 47.1275 48.7817 48.0211 48.7817 48.0211H56.7265V13.5125Z"
        fill="#2E3E54"
      />
      <Path
        d="M23.7174 41.4252C23.7174 30.8553 16.8669 29.8775 12.597 30.2385C12.6915 30.117 12.7685 30.0177 12.779 30.0014L19.9877 19.8205C21.8321 17.2154 21.0283 15.5893 20.9933 15.5215C20.9863 15.5075 20.977 15.4946 20.9653 15.483L18.3509 12.7552C18.2937 12.6956 18.2039 12.6886 18.1385 12.7377C18.0732 12.7868 18.0534 12.8779 18.0907 12.9503C18.0977 12.9655 18.8409 14.4538 17.1084 16.9L10.1657 26.7035C10.1552 23.623 10.1552 19.845 10.1552 19.7212C10.1552 14.2821 7.27822 13.3931 7.27822 13.3931H0.5C0.5 13.3931 3.37578 14.2295 3.37578 19.7212V41.4252C3.37578 46.8643 6.25274 47.7533 6.25274 47.7533H13.0333C13.0333 47.7533 10.1563 46.9169 10.1563 41.4252V32.1158C12.1595 31.8541 16.9369 32.1684 16.9369 41.4252C16.9369 46.8643 19.8127 47.7533 19.8127 47.7533H26.5932C26.5932 47.7533 23.7163 46.9169 23.7163 41.4252H23.7174Z"
        fill="#2E3E54"
      />
      <Path
        d="M50.4709 0.5C44.3157 0.5 38.8103 2.99995 35.1365 6.93328C38.5256 3.8796 43.3392 2.18338 48.5436 2.60743C57.8348 3.36326 64.9747 10.6715 64.9747 18.9692V53.4043L30.4431 56.3014V56.5H70.4999V18.5312C70.4988 8.57343 61.5319 0.5 50.4709 0.5Z"
        fill="#2E3E54"
      />
    </Svg>
  );

  const HotelInfo = () => (
    <View style={styles.titleContainer}>
      <View style={styles.spaceBetween}>
        <View>
          <Text style={{ ...styles.textBold, textTransform: 'uppercase' }}>
            {currentBranch?.name}
          </Text>
          <Text style={styles.text}>Address: {currentBranch?.address}</Text>
          <Text style={styles.text}>
            {currentBranch?.phone_contact ? `Tel: ${currentBranch?.phone_contact}` : ''}
          </Text>
        </View>
        <Logo />
      </View>
    </View>
  );

  const BillInfo = () => (
    <View style={styles.billContainer}>
      <View
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '16px'
        }}
      >
        <Text style={{ ...styles.textBold, fontSize: 20 }}>HÓA ĐƠN</Text>
        <Text style={{ fontSize: 14 }}>(INVOICE)</Text>
      </View>

      <View style={styles.billRow}>
        <View style={{ ...styles.billCol, width: '40%' }}>
          <View style={styles.billColItem}>
            <Text style={styles.text}>Ngày (Date):</Text>
            <Text style={styles.textExSmallBold}>
              {dataChange?.createdDate?.format('DD/MM/YYYY')}
            </Text>
          </View>
        </View>
        <View style={{ ...styles.billCol, width: '60%' }}>
          <View style={styles.billColItem}>
            <Text style={{ ...styles.text, width: '65%' }}>Số tham chiếu (Folio No.):</Text>
            <Text style={{ ...styles.textExSmallBold, width: '35%' }}>
              {orderDetail?.sale_order_name}
            </Text>
          </View>
          <View style={styles.billColItem}>
            <Text style={{ ...styles.text, width: '65%' }}>Số phòng (Room No.):</Text>
            <Text style={{ ...styles.textExSmallBold, width: '35%' }}>
              {bookingLineDetail?.room_name}
            </Text>
          </View>
        </View>
      </View>

      <View style={styles.billRowItem}>
        <Text style={{ ...styles.text }}>Tên khách hàng (Guest Name):</Text>
        <Text style={{ ...styles.textExSmallBold }}>
          {dataChange?.partnerName || dataGroup?.name}
        </Text>
      </View>

      <View style={styles.billRowItem}>
        <Text style={{ ...styles.text }}>Công ty (Company):</Text>
        <Text style={{ ...styles.textExSmallBold }}>{dataChange?.company}</Text>
      </View>

      <View style={{ ...styles.billRow, marginTop: '8px' }}>
        <View style={{ ...styles.billCol, width: '47%' }}>
          <View style={styles.billColItem}>
            <Text style={styles.text}>Mã số thuế (Tax Code):</Text>
            <Text style={styles.textExSmallBold}>{dataChange?.taxCode}</Text>
          </View>
        </View>
      </View>

      <View style={{ ...styles.billRow, marginTop: '8px' }}>
        <View style={{ ...styles.billCol, width: '47%' }}>
          <View style={styles.billColItem}>
            <Text style={styles.text}>CMND/CCCD:</Text>
            <Text style={styles.textExSmallBold}>{dataChange?.idCard}</Text>
          </View>
        </View>
        <View style={{ ...styles.billCol, width: '53%' }}>
          <View style={styles.billColItem}>
            <Text style={styles.text}>Hộ chiếu (Passport):</Text>
            <Text style={styles.textExSmallBold}>{dataChange?.passport}</Text>
          </View>
        </View>
      </View>

      <View style={{ ...styles.billRow, marginTop: '8px' }}>
        <View style={{ ...styles.billCol, width: '60%' }}>
          <View style={{ ...styles.billColItem }}>
            <Text style={styles.text}>Email:</Text>
            <Text style={styles.textExSmallBold}>{dataChange?.email}</Text>
          </View>
        </View>
      </View>

      <View style={{ ...styles.billRow, marginTop: '8px' }}>
        <View style={{ ...styles.billCol, width: '80%' }}>
          <View style={{ ...styles.billColItem }}>
            <Text style={styles.text}>Email Cc:</Text>
            <Text style={styles.textExSmallBold}>{dataChange?.emailCc}</Text>
          </View>
        </View>
      </View>

      <View style={{ ...styles.billRowItem, marginTop: '8px' }}>
        <View style={{ ...styles.billCol, width: '80%' }}>
          <View style={{ ...styles.billColItem, columnGap: '26px' }}>
            <Text style={styles.text}>Địa chỉ (Address):</Text>
            <Text style={styles.textExSmallBold}>{dataChange?.companyAddress}</Text>
          </View>
        </View>
      </View>
    </View>
  );

  const Signature = () => (
    <View style={styles.signatureContainer}>
      <View style={styles.signatureItem}>
        <Text style={styles.textSmallBold}>Khách hàng (Customer):</Text>
        <Text style={styles.text}>Ký và ghi rõ họ tên (Sign and full name)</Text>
        {signatureGuest && <Image style={styles.image} src={signatureGuest} />}
      </View>
      <View style={styles.signatureItem}>
        <Text style={styles.textSmallBold}>Thu ngân (Cashier):</Text>
        <Text style={styles.text}>Ký và ghi rõ họ tên (Sign and full name)</Text>
        {signatureStaff && <Image style={styles.image} src={signatureStaff} />}
      </View>
    </View>
  );

  const totalPaidAmount = useMemo(() => {
    return (
      transactionPayment
        ?.filter(payment => !payment.is_deleted)
        .reduce((cur, next) => {
          return cur + next.amount;
        }, 0) || 0
    );
  }, [transactionPayment]);

  const totalBalanceDeposit = useMemo(() => {
    return (
      dataGroup?.sub_groups
        ?.filter(item => item.dummy_booking)
        .reduce((cur, next) => {
          const balanceSum = next.dummy_booking.balance_dummy_booking.reduce(
            (acc, value) => acc + value,
            0
          );
          return cur + balanceSum;
        }, 0) || 0
    );
  }, [dataGroup?.sub_groups]);

  const groupItems = useMemo(() => {
    return dataGroup?.sub_groups.map(item => {
      const totalPrice = item.dummy_booking
        ? totalBalanceDeposit
        : item.count_date * item.room_price * item.quantity;

      if (item.dummy_booking) {
        return {
          check_in: '',
          check_out: '',
          room_type_name: 'Tiền đặt cọc',
          quantity: '',
          pax: '',
          count_date: '',
          room_price: '',
          total_price: totalPrice
        };
      }

      return {
        check_in: toLocalTime(item.check_in, 'DD/MM/YYYY'),
        check_out: toLocalTime(item.check_out, 'DD/MM/YYYY'),
        room_type_name: item.room_type_name,
        price: item.room_price.toLocaleString('vn'),
        quantity: item.quantity,
        pax: item.pax,
        count_date: item.count_date,
        room_price: item.room_price,
        total_price: totalPrice
      };
    });
  }, [dataGroup?.sub_groups, totalBalanceDeposit]);

  const totalBalanceAndPrice = useMemo(() => {
    let totalBalanceDeposit = 0;
    let totalItemPrice = 0;

    dataGroup?.sub_groups?.forEach(item => {
      if (item.dummy_booking) {
        const balanceSum = item.dummy_booking.balance_dummy_booking.reduce(
          (acc, value) => acc + value,
          0
        );
        totalBalanceDeposit += balanceSum;
      } else {
        totalItemPrice += item.count_date * item.room_price * item.quantity;
      }
    });

    return {
      total: totalBalanceDeposit + totalItemPrice,
      totalItemPrice
    };
  }, [dataGroup?.sub_groups]);

  const paymentItems = useMemo(() => {
    const { chargeType } = dataChange;
    let totalAmount = 0;

    let bookingPrices =
      orderDetail?.transactions
        .filter(
          item => !item.is_deleted && !PAYMENT_AND_REFUND_TRANSACTION_CODE.includes(item.code)
        )
        .map(item => {
          const subAmount = item.tax_excluded;
          const serviceFee = item.service_charge;
          const tax = item.tax_amount;
          totalAmount += item.total;
          return {
            date: toLocalTime(item.transaction_date, 'DD/MM/YYYY'),
            detail: item.name,
            price: (subAmount - serviceFee).toLocaleString('vn'),
            qty: item.qty,
            serviceFee: serviceFee.toLocaleString('vn'),
            [`tax-${item.tax}`]: tax.toLocaleString('vn'),
            amount: item.total.toLocaleString('vn')
          };
        }) || [];

    if (chargeType === CHARGE_TYPE.SERVICE || chargeType === CHARGE_TYPE.ROOM) {
      bookingPrices = [];
      totalAmount = 0;
    }

    let services: any[] = [];
    if (chargeType === CHARGE_TYPE.SERVICE) {
      services =
        orderDetail?.transactions
          .filter(item => SERVICE_TRANSACTION_CODE.includes(item.code) && !item.is_deleted)
          .map(item => {
            const subAmount = item.tax_excluded;
            const serviceFee = item.service_charge;
            const tax = item.tax_amount;
            totalAmount += item.total;
            return {
              date: toLocalTime(item.transaction_date, 'DD/MM/YYYY'),
              detail: item.name,
              price: subAmount.toLocaleString('vn'),
              qty: item.qty,
              serviceFee: serviceFee.toLocaleString('vn'),
              [`tax-${item.tax}`]: tax.toLocaleString('vn'),
              amount: item.total.toLocaleString('vn')
            };
          }) || [];
    }

    let filteredPayments: any[] = [];
    if (chargeType === CHARGE_TYPE.ROOM) {
      filteredPayments =
        orderDetail?.transactions
          .filter(
            item =>
              !SERVICE_TRANSACTION_CODE.includes(item.code) &&
              !item.is_deleted &&
              !PAYMENT_AND_REFUND_TRANSACTION_CODE.includes(item.code)
          )
          .map(item => {
            const subAmount = item.tax_excluded;
            const serviceFee = item.service_charge;
            const tax = item.tax_amount;
            totalAmount += item.total;
            return {
              date: toLocalTime(item.transaction_date, 'DD/MM/YYYY'),
              detail: item.name,
              price: subAmount.toLocaleString('vn'),
              qty: item.qty,
              serviceFee: serviceFee.toLocaleString('vn'),
              [`tax-${item.tax}`]: tax.toLocaleString('vn'),
              amount: item.total.toLocaleString('vn')
            };
          }) || [];
    }

    const result = [...bookingPrices, ...services, ...filteredPayments];
    return {
      items: result,
      totalAmount: totalAmount
    };
  }, [dataChange, orderDetail]);

  const totalPrice = useMemo(() => {
    const { chargeType } = dataChange;
    let total = 0;
    const totalPriceAll =
      orderDetail?.transactions
        .filter(item => !item.is_deleted)
        .reduce((cur, next) => {
          return cur + next.total;
        }, 0) || 0;
    const totalBookingPrice =
      orderDetail?.transactions
        .filter(item => !SERVICE_TRANSACTION_CODE.includes(item.code) && !item.is_deleted)
        .reduce((cur, next) => {
          return cur + next.total;
        }, 0) || 0;

    const totalServicePrice =
      orderDetail?.transactions
        .filter(item => !ROOM_CHARGE_TRANSACTION_CODE.includes(item.code) && !item.is_deleted)
        .reduce((cur, next) => {
          return cur + next.total;
        }, 0) || 0;

    switch (chargeType) {
      case CHARGE_TYPE.ALL:
        total = totalPriceAll;
        break;
      case CHARGE_TYPE.ROOM:
        total = totalBookingPrice;
        break;
      case CHARGE_TYPE.SERVICE:
        total = totalServicePrice;
        break;
      default:
        break;
    }
    return total;
  }, [dataChange, orderDetail]);

  const listTax = useMemo(() => {
    const result = {};
    const { chargeType } = dataChange;

    if (!_.isEmpty(orderDetail)) {
      if (
        _.isEmpty(
          _.get(
            result,
            orderDetail?.transactions?.map(item => item.tax),
            ''
          )
        ) &&
        [CHARGE_TYPE.ALL, CHARGE_TYPE.ROOM].includes(chargeType)
      ) {
        _.set(
          result,
          orderDetail?.transactions?.map(item => item.tax),
          true
        );
      }

      if ([CHARGE_TYPE.ALL, CHARGE_TYPE.SERVICE].includes(chargeType)) {
        orderDetail?.transactions
          .filter(item => SERVICE_TRANSACTION_CODE.includes(item.code) && !item.is_deleted)
          .forEach(item => {
            if (_.isEmpty(_.get(result, item.tax, ''))) {
              _.set(result, item.tax, true);
            }
          });
      }
    }
    return result;
  }, [dataChange, orderDetail]);

  return (
    <Document title={t('common.bookingData.receipt')} language="vi">
      <Page wrap size="A4" style={styles.page}>
        <View style={showInvoiceError ? { opacity: 0.3 } : {}}>
          <HotelInfo />
          <BillInfo />

          <InvoiceItemsTable
            listTax={listTax}
            paymentItems={paymentItems.items}
            remainAmount={dataGroup ? totalBalanceAndPrice.total : totalPrice}
            forGroup={!!dataGroup}
            groupItems={groupItems}
            transactionPayment={transactionPayment}
            paidAmount={totalPaidAmount}
            totalPrice={
              dataGroup
                ? totalBalanceAndPrice.totalItemPrice.toLocaleString('vn')
                : paymentItems.totalAmount.toLocaleString('vn')
            }
          />

          <View
            break={paymentItems.items.length >= 8}
            style={{
              ...styles.totalRow,
              marginTop: '12px',
              width: '80%'
            }}
          >
            <Text style={styles.text}>Tổng cộng tiền thanh toán (Grand total):</Text>
            <Text style={{ ...styles.text, fontSize: 12 }}>
              {dataGroup
                ? totalBalanceAndPrice.totalItemPrice.toLocaleString('vn')
                : paymentItems.totalAmount.toLocaleString('vn')}
            </Text>
          </View>

          <View style={{ ...styles.flex, marginTop: '6px' }}>
            <Text style={styles.text}>Số tiền bằng chữ (Amount in words):</Text>
            <Text style={styles.text}>
              {dataGroup
                ? currencyFormatter(totalBalanceAndPrice.totalItemPrice)
                : currencyFormatter(paymentItems.totalAmount)}
            </Text>
          </View>

          <Signature />
        </View>

        {showInvoiceError && invoiceError && (
          <View
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              paddingBottom: 220,
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.1)'
            }}
          >
            <View
              style={{
                backgroundColor: '#FF0000',
                padding: 30,
                borderRadius: 10,
                alignItems: 'center',
                justifyContent: 'center',
                width: '80%',
                maxWidth: 500,
                minHeight: 120
              }}
            >
              <Text
                style={{
                  color: '#FFFFFF',
                  fontSize: 18,
                  fontWeight: 'bold',
                  textAlign: 'center',
                  fontFamily: 'Inter',
                  lineHeight: 1.5
                }}
              >
                {invoiceError}
              </Text>
            </View>
          </View>
        )}
      </Page>
    </Document>
  );
}
